import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  IoCardOutline,
  IoMapOutline,
  IoTimeOutline,
  IoBusOutline,
  IoSchoolOutline,
} from "react-icons/io5";
import LastTransaction from "../components/LastTransaction";
import ProgressBar from "../components/ProgressBar";
import InfoCard from "../components/Infocard";
import HomeSkeleton from "../components/skeleton-loading/HomeSkeleton";

import { useData } from "../data/DataProvider";

export default function Home() {
  const navigate = useNavigate();
  const [showAll, setShowAll] = useState(false);

  const {
    studentData,
    activeRouteData,
    paymentHistoryData,
    installmentsSummaryData,
    getAllData,
    loading,
  } = useData();

  useEffect(() => {
    const forceRefresh = Boolean(location.state?.forceRefresh);
    getAllData(forceRefresh);
  }, [location.state]);

  const routesList = Array.isArray(installmentsSummaryData)
    ? installmentsSummaryData
    : [];
  const currentActiveRouteId = activeRouteData?.student_route_id;

  const routeInfo =
    routesList.find((r) =>
      (r.installments || []).some(
        (ins) => ins.status === "unpaid" || ins.status === "partial",
      ),
    ) ||
    routesList.find((r) => r.student_route_id === currentActiveRouteId) ||
    routesList[0] ||
    null;

  const allInstallments = routeInfo?.installments || [];

  const nextUnpaidInstallment = allInstallments.find(
    (ins) => ins.status === "unpaid" || ins.status === "partial",
  );

  const paidCount = allInstallments.filter(
    (ins) => ins.status === "paid",
  ).length;
  const progressPercentage =
    allInstallments.length > 0
      ? Math.round((paidCount / allInstallments.length) * 100)
      : 0;

  const upcomingAmount =
    progressPercentage === 100
      ? 0
      : nextUnpaidInstallment
        ? Number(
            nextUnpaidInstallment.balance ??
              (Number(nextUnpaidInstallment.amount || 0) -
                Number(nextUnpaidInstallment.paid_amount || 0)),
          )
        : 0;

  let remainingDays = null;
  if (nextUnpaidInstallment?.due_date && upcomingAmount > 0) {
    const dueDate = new Date(nextUnpaidInstallment.due_date);
    const today = new Date();
    const timeDiff = dueDate.getTime() - today.getTime();
    remainingDays = Math.ceil(timeDiff / (1000 * 3600 * 24));
  }

  const historyRoutesList = Array.isArray(paymentHistoryData)
    ? paymentHistoryData
    : [];
  const historyRouteInfo =
    historyRoutesList.find(
      (r) => r.student_route_id === routeInfo?.student_route_id,
    ) ||
    historyRoutesList.find(
      (r) => r.student_route_id === currentActiveRouteId,
    ) ||
    historyRoutesList[0] ||
    null;

  const completedPayments = historyRouteInfo?.completed_payments || [];

  const studentFullName =
    routeInfo?.student_name ||
    activeRouteData?.student_name ||
    (studentData?.name && studentData?.surname
      ? `${studentData.name} ${studentData.surname}`
      : studentData?.name || "");

  const dynamicTransactions = completedPayments.map((pay) => {
    const formattedDate = pay.paid_at
      ? new Date(pay.paid_at).toLocaleDateString("tr-TR", {
          day: "numeric",
          month: "long",
          year: "numeric",
        })
      : "-";

    const paidAmt = Number(pay.paid_amount || 0);
    const totalAmt = Number(pay.amount || 0);
    const remainingBal = Number(pay.balance ?? (totalAmt - paidAmt));

    const isPartial =
      pay.status === "partial" ||
      (remainingBal > 0 && pay.status !== "paid") ||
      (totalAmt > 0 && paidAmt < totalAmt && pay.status !== "paid");

    return {
      title: `${pay.installment_number}. Taksit Ödemesi`,
      date: formattedDate,
      amount: paidAmt
        ? parseFloat(paidAmt).toLocaleString("tr-TR", {
            minimumFractionDigits: 2,
          })
        : "0,00",
      status: isPartial ? "KISMİ ÖDEME" : "ÖDENDİ",
    };
  });

  const displayedTransactions = showAll
    ? dynamicTransactions
    : dynamicTransactions.slice(0, 3);

  const formatTime = (timeStr) => (timeStr ? timeStr.slice(0, 5) : "");

  let serviceTime = "-";
  if (activeRouteData) {
    const isActiveReturn = activeRouteData.active_type === "return";
    const startTime = formatTime(
      isActiveReturn
        ? activeRouteData.return_start_time
        : activeRouteData.departure_start_time,
    );
    const endTime = formatTime(
      isActiveReturn
        ? activeRouteData.return_end_time
        : activeRouteData.departure_end_time,
    );

    if (startTime && endTime) {
      serviceTime = `${startTime} - ${endTime}`;
    }
  }

  const handleQuickPay = () => {
    const targetRouteId =
      routeInfo?.student_route_id ||
      routeInfo?.route_id ||
      routeInfo?.id ||
      activeRouteData?.student_route_id;

    const targetId =
      nextUnpaidInstallment?.id ||
      nextUnpaidInstallment?.installment_number;

    navigate("/installment", {
      state: {
        targetRouteId: targetRouteId,
        targetInstallmentId: targetId,
        autoOpenPayment: true,
        autoOpenPay: true,
      },
    });
  };

  if (loading && !studentData) {
    return <HomeSkeleton />;
  }

  return (
    <div className="min-h-screen bg-[#f8f9ff] text-[#0b1c30] antialiased pb-4">
      <div className="max-w-4xl mx-auto px-4 pt-6 md:pt-10">
        <div className="mb-6">
          <p className="text-gray-500 text-base md:text-lg font-medium">Hoş geldiniz,</p>
          <h1 className="text-2xl md:text-3xl font-bold text-[#00306b] mt-0.5">
            {(`${studentData?.parent?.name || ""}` + " " + `${studentData?.parent?.surname || ""}`).trim() || "Değerli Velimiz"}
          </h1>
          {studentFullName ? (
            <div className="flex items-center mt-2.5 bg-blue-50 self-start px-3.5 py-2 rounded-xl border border-blue-200 w-fit">
              <IoSchoolOutline className="text-lg text-[#00306b]" />
              <span className="text-sm text-[#00306b] font-medium ml-2">
                Öğrenci: <strong className="font-bold text-base text-[#00306b]">{studentFullName}</strong>
              </span>
            </div>
          ) : null}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:items-stretch">
          <div className="bg-white rounded-2xl p-5 md:p-6 shadow-[0px_4px_20px_rgba(0,0,0,0.03)] border border-gray-100 flex flex-col justify-between h-full">
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-gray-500 text-sm font-medium">
                  Gelecek Ödeme
                </span>
                {remainingDays !== null && remainingDays > 0 && (
                  <span
                    className={`text-xs font-semibold px-3 py-1 rounded-full ${
                      remainingDays <= 7
                        ? "bg-red-50 text-red-500"
                        : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {remainingDays <= 7
                      ? `Son ${remainingDays} Gün`
                      : `${remainingDays} Gün`}
                  </span>
                )}
              </div>

              <h2 className="text-2xl md:text-3xl font-bold text-[#0b1c30]">
                {upcomingAmount > 0
                  ? `₺${upcomingAmount.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}`
                  : "-"}
              </h2>

              <ProgressBar
                title="Ödeme Plan İlerlemesi"
                percentage={progressPercentage}
                showPercentage={true}
                barColor="#00306b"
                trackColor="#e0e0e0"
              />
            </div>

            <button
              onClick={handleQuickPay}
              className="w-full bg-tuncayBlue hover:bg-[#0046b3] text-white py-3 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-colors shadow-sm cursor-pointer"
            >
              <IoCardOutline className="text-lg" />
              Hızlı Öde
            </button>
          </div>

          <div className="space-y-4 flex flex-col justify-between">
            <div className="bg-tuncayBlue text-white rounded-2xl p-5 shadow-[0px_4px_20px_rgba(17,78,178,0.15)] flex-1 flex flex-col justify-between min-h-[170px]">
              <div>
                <div className="flex items-center gap-1.5 text-white/70 text-xs font-medium mb-1">
                  <IoMapOutline className="text-sm" />
                  Aktif Güzergah
                </div>

                <h3 className="text-base md:text-lg font-bold mb-5 tracking-wide mt-2">
                  {activeRouteData?.route_name || "Aktif bir rota bulunmuyor."}
                </h3>
              </div>

              {activeRouteData && (
                <div className="flex items-center justify-between border-t border-white/10 pt-4">
                  <div className="w-full flex justify-between items-center gap-4">
                    <div className="flex items-center gap-3">
                      <div>
                        <p className="text-sm font-semibold leading-tight">
                          {activeRouteData.driver?.name || "-"}
                        </p>
                        <p className="text-[11px] text-white/60 uppercase tracking-wider font-medium mt-0.5">
                          Servis Şoförü
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <div>
                        <p className="text-sm font-semibold leading-tight">
                          {activeRouteData.hostess?.name || "-"}
                        </p>
                        <p className="text-[11px] text-white/60 uppercase tracking-wider font-medium mt-0.5">
                          Servis Hostesi
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {activeRouteData && (
              <div className="grid grid-cols-2 gap-4">
                <InfoCard
                  icon={<IoTimeOutline />}
                  title="Servis Saati"
                  value={serviceTime}
                />
                <InfoCard
                  icon={<IoBusOutline />}
                  title="Plaka"
                  value={activeRouteData.car?.plaka_no || "-"}
                />
              </div>
            )}
          </div>
        </div>

        <div className="mt-8">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-base md:text-lg font-bold text-gray-800 tracking-tight">
              Son İşlemler
            </h3>
            {dynamicTransactions.length > 3 && (
              <button
                onClick={() => setShowAll(!showAll)}
                className="text-sm font-semibold text-[#0052cc] hover:underline focus:outline-none cursor-pointer"
              >
                {showAll ? "Daha Az Göster" : "Tümünü Gör"}
              </button>
            )}
          </div>

          <div className="space-y-3 mb-4">
            {displayedTransactions.length > 0 ? (
              displayedTransactions.map((item, index) => (
                <LastTransaction
                  key={index}
                  title={item.title}
                  date={item.date}
                  amount={item.amount}
                  status={item.status}
                />
              ))
            ) : (
              <p className="text-sm text-gray-400 italic text-center py-4">
                Henüz gerçekleşen bir işlem bulunmuyor.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
